import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  FocusEvent,
  MouseEvent,
  KeyboardEvent,
  WheelEvent,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import './Input.scss';

interface InputProps {
  type?: string;
  value?: any;
  onChange?: (value: string | boolean) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onWheel?: (e: WheelEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  maxLength?: number;
  minLength?: number;
  title?: string;
  placeholder?: string;
  step?: string;
  checked?: boolean;
  disabled?: boolean;
  required?: boolean;
}

const DEBOUNCE_THRESHOLD_IN_MS = 500;

const Input: FunctionComponent<InputProps> = props => {
  const [displayValue, setDisplayValue] = useState(props.value);
  const [pendingValue, setPendingValue] = useState(props.value);
  const [hasFocus, setHasFocus] = useState(false);

  useEffect(() => {
    // do not update the input field while it has the focus
    if (hasFocus) {
      setPendingValue(props.value);
    } else if (props.value !== displayValue) {
      setDisplayValue(props.value);
    }
  }, [props.value]);

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((newValue: string | boolean) => {
        if (props.onChange) {
          props.onChange(newValue);
        }
      }, DEBOUNCE_THRESHOLD_IN_MS),
    [props.onChange],
  );

  const onFocusHandler = (e: FocusEvent<HTMLInputElement>) => {
    setHasFocus(true);
    setPendingValue(displayValue);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const onBlurHandler = (e: FocusEvent<HTMLInputElement>) => {
    setHasFocus(false);
    if (pendingValue !== displayValue) {
      setDisplayValue(pendingValue);
    }
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const onWheelHandler = (e: WheelEvent<HTMLInputElement>) => {
    if (props.onWheel) {
      props.onWheel(e);
    } else {
      (e.target as HTMLElement).blur();
    }
  };

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    const newValue = props.type === 'checkbox' ? checked : value;
    setDisplayValue(newValue);
    if (props.onChange) {
      debouncedChangeHandler(newValue);
    }
  };

  return (
    <input
      {...props}
      type={props.type}
      value={displayValue}
      onChange={changeHandler}
      onFocus={onFocusHandler}
      onBlur={onBlurHandler}
      onWheel={onWheelHandler}
      onClick={props.onClick}
      className={`pencil-input ${props.className}`}
      maxLength={props.maxLength}
      minLength={props.minLength}
      title={props.title}
      placeholder={props.placeholder}
      min={0}
      step={props.step}
      onKeyDown={props.onKeyDown}
      checked={props.checked}
      disabled={props.disabled}
      required={props.required}
    />
  );
};

Input.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  className: PropTypes.string,
  maxLength: PropTypes.number,
  minLength: PropTypes.number,
  title: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  step: PropTypes.string,
  onKeyDown: PropTypes.func,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

Input.defaultProps = {
  type: 'text',
  value: '',
  onChange: () => {},
  className: '',
  placeholder: '',
};

export default Input;
